"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";
import { X, SlidersHorizontal } from "lucide-react";

type Category = { id: string; name: string };

export default function ActualitesFilters({
  categories,
  totalResults,
}: {
  categories: Category[];
  totalResults: number;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const activeCats = searchParams.get("cat")?.split(",").filter(Boolean) ?? [];
  const hasFilters = activeCats.length > 0;

  const push = useCallback(
    (cats: string[]) => {
      const next = new URLSearchParams(searchParams.toString());
      if (cats.length === 0) next.delete("cat");
      else next.set("cat", cats.join(","));
      router.push(`/actualites?${next.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const toggle = (id: string) => {
    const next = activeCats.includes(id)
      ? activeCats.filter((c) => c !== id)
      : [...activeCats, id];
    push(next);
  };

  return (
    <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <SlidersHorizontal size={15} className="text-slate-400" />
          Filtrer par catégorie
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">
            {totalResults} article{totalResults !== 1 ? "s" : ""}
          </span>
          {hasFilters && (
            <button
              onClick={() => push([])}
              className="flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100"
            >
              <X size={12} /> Effacer
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => {
          const active = activeCats.includes(cat.id);
          return (
            <button
              key={cat.id}
              onClick={() => toggle(cat.id)}
              className={[
                "rounded-full px-3.5 py-1.5 text-sm font-medium transition",
                active
                  ? "bg-[#1a2744] text-white"
                  : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50",
              ].join(" ")}
            >
              {cat.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
